import {CartItem} from 'epic-graphql-types';
import {isNil} from 'lodash';
import {PurchaseOfferType, PurchaseReceipt} from '@/types';
import LauncherIntegrationUtils from 'epic-launcher-integration/app/scripts/utils/LauncherIntegrationUtils';
import {APP_CONTEXT} from '@/constants';

export interface LineOffer {
	offerNamespace: string;
	offerId: string;
	quantity: number;
}

export interface Asset {
	locale: string;
	canPurchase?: boolean;
	id: string;
	namespace: string;
	offerType?: PurchaseOfferType;
	merchantGroup?: string;
	salesChannel?: string;
}

/**
 * Handle Single Offer
 */
const onBuyNow = function onBuyNow(
	asset: Asset,
	onSuccess: (receipt: PurchaseReceipt) => void,
	// NOTE: the purchase flow does not return proper Error objects to the reject function
	onRejected: (reason: string) => void
): void {
	if (!asset) {
		return onRejected('No offer found');
	}

	if (asset.canPurchase === false) {
		return onRejected('Offer cannot be purchased');
	}

	console.log('onClickBuy', 'starting purchase flow v2', {asset});

	if (!isNil(window)) {
		window.purchaseBaseUrl = `${APP_CONTEXT}/api`;
	}

	const promise = LauncherIntegrationUtils.requestPurchaseV2({
		lineOffers: [
			{
				offerNamespace: asset.namespace,
				offerId: asset.id,
				offerType: asset.offerType,
				quantity: 1
			}
		],
		locale: asset.locale,
		fallback: null,
		highlightColor: '0078f2',
		showNavigation: true,
		salesChannel: asset.salesChannel,
		merchantGroup: asset.merchantGroup
	}) as Promise<PurchaseReceipt>;

	promise.then(onSuccess, onRejected);
};

export const buyAssetOrSignIn = (asset: Asset): Promise<PurchaseReceipt> => {
	return new Promise((resolve, reject) => {
		onBuyNow(asset, resolve, reject);
	});
};

/**
 * Handle multiple offers
 */
export const onStartCheckout = (
	lineOffers: LineOffer[],
	locale: string
): Promise<PurchaseReceipt> => {
	console.log(`onStartCheckout", "starting purchase flow v2 for checkout [${locale}]`);

	if (!isNil(window)) {
		window.purchaseBaseUrl = `${APP_CONTEXT}/api`;
	}

	return LauncherIntegrationUtils.requestPurchaseV2({
		locale,
		lineOffers,
		fallback: null,
		highlightColor: '0078f2',
		showNavigation: true
	}) as Promise<PurchaseReceipt>;
};

export const getLineOffersFromCart = (cartItems: CartItem[] | undefined) => {
	if (!cartItems) {
		return undefined;
	}
	return cartItems.length > 0
		? cartItems?.map((cartItem) => ({
				namespace: cartItem.namespace,
				offerId: cartItem.offerId,
				quantity: cartItem.quantity
			}))
		: [];
};

/** Purchase utils use `offerNamespace` as the property name */
export const getPurchaseLineOffersFromCart = (cartItems: CartItem[] | undefined) => {
	if (!cartItems) {
		return undefined;
	}
	return cartItems.length > 0
		? cartItems?.map((cartItem) => ({
				offerNamespace: cartItem.namespace,
				offerId: cartItem.offerId,
				quantity: cartItem.quantity
			}))
		: [];
};

interface Sandboxable {
	catalogId?: string;
	catalogid?: string;
	catalogItemId?: string;
	id?: string | null;
	namespace?: string | null;
	offerId?: string;
	gameId?: string;
	sandboxId?: string;
}

/**
 * Example: Working redirect for guest user to login and resurface spend page
 *
 * https://www.epicgames.com/id/login
 * ?lang=en_US
 * &client_id=<CLIENT_ID>
 * &source=fgc
 * &redirectUrl=https://www.epicgames.com/redeem/spend?purchaseIntentId=fn-ede05b3c97e9475a8d9be91da65750f0
 *
 * Note: namespace|offerId is rejected because of '|' but '-' works lol
 */
export const getSandboxId = (x: Sandboxable): string => {
	if (x.sandboxId) {
		return x.sandboxId;
	}
	const namespace = x.namespace;
	let catalogId = x.catalogId || x.catalogid || x.catalogItemId || x.id || x.offerId || x.gameId;
	if (!catalogId) {
		console.warn(`Unable to generate a sandbox id for the given item (${JSON.stringify(x)})`);
		catalogId = 'NA';
	}
	if (namespace && catalogId?.includes(namespace)) {
		//already a sandbox id
		return catalogId;
	}
	if (!namespace) {
		return catalogId;
	}
	return `${namespace}-${catalogId}`;
};
